import { useCallback, useMemo, useState } from "react"

import { ScopedTestMetricDrawer } from "@/metrics"
import { useMetricsList } from "@/metrics/catalog"
import { isExpectedBehaviorCompositeMetric } from "@/metrics/registry"
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@coval/ui"
import { ChevronDown, ChevronUp, X } from "lucide-react"

import type { EditableTestCase } from "../model"
import { useAnnotatedTestCaseConversationIds } from "../routes/useAnnotatedTestCaseConversationIds"
import { usePersistExpectedBehaviors } from "../routes/usePersistExpectedBehaviors"

function ExpectedBehaviorScopeSummary({
  testCase,
}: {
  testCase: EditableTestCase
}) {
  const [isExpanded, setIsExpanded] = useState(true)
  const title = testCase.description?.trim() || "Test case"
  const behaviors = testCase.expected_behaviors

  return (
    <section
      aria-label="Expected behaviors for this test case"
      className="border-b border-coval-border bg-surface-1 px-4 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-coval-text-muted">
            Current expected behaviors
          </p>
          <h3 className="mt-0.5 truncate text-sm font-medium text-coval-text">
            {title}
          </h3>
          <p className="mt-1 line-clamp-2 text-xs text-coval-text-muted">
            {testCase.input_str}
          </p>
          <p className="mt-2 text-xs text-coval-text-muted">
            Run a new test to compare these behaviors with each conversation's
            previous saved result.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          className="h-7 shrink-0 px-2 text-xs"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {behaviors.length} {behaviors.length === 1 ? "behavior" : "behaviors"}
          {isExpanded ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>

      {isExpanded && (
        <ol className="mt-3 space-y-2" aria-label="Expected behaviors">
          {behaviors.length > 0 ? (
            behaviors.map((behavior, index) => (
              <li
                key={`${index}:${behavior}`}
                className="flex items-start gap-2 text-sm text-coval-text"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-2 text-[11px] font-medium text-coval-text-muted">
                  {index + 1}
                </span>
                <span className="min-w-0 break-words">{behavior}</span>
              </li>
            ))
          ) : (
            <li className="text-sm text-coval-text-muted">
              No expected behaviors added yet.
            </li>
          )}
        </ol>
      )}
    </section>
  )
}

export function TestSetExpectedBehaviorDrawer({
  onClose,
  testCase,
  testSetId,
  onRunningChange,
}: {
  onClose: () => void
  testCase: EditableTestCase
  testSetId: string
  onRunningChange: (isRunning: boolean) => void
}) {
  const [selectedMetricId, setSelectedMetricId] = useState("")
  const [isRunning, setIsRunning] = useState(false)
  const [runCriteriaSnapshot, setRunCriteriaSnapshot] = useState<{
    testCaseId: string
    metricId: string
    behaviors: string[]
  } | null>(null)
  const persistExpectedBehaviorsForTestCase = usePersistExpectedBehaviors()
  const activeCriteriaSnapshot =
    runCriteriaSnapshot?.testCaseId === testCase.id &&
    runCriteriaSnapshot.metricId === selectedMetricId
      ? runCriteriaSnapshot.behaviors
      : null
  const displayedTestCase = useMemo(
    () =>
      activeCriteriaSnapshot
        ? { ...testCase, expected_behaviors: activeCriteriaSnapshot }
        : testCase,
    [activeCriteriaSnapshot, testCase],
  )
  const persistExpectedBehaviors = useCallback(async () => {
    const behaviors = [...testCase.expected_behaviors]
    const snapshot = {
      testCaseId: testCase.id,
      metricId: selectedMetricId,
      behaviors,
    }
    setRunCriteriaSnapshot(snapshot)
    try {
      await persistExpectedBehaviorsForTestCase({
        ...testCase,
        expected_behaviors: behaviors,
      })
    } catch (error) {
      setRunCriteriaSnapshot((current) =>
        current === snapshot ? null : current,
      )
      throw error
    }
  }, [persistExpectedBehaviorsForTestCase, selectedMetricId, testCase])
  const handleRunningChange = useCallback(
    (running: boolean) => {
      setIsRunning(running)
      if (!running) {
        setRunCriteriaSnapshot(null)
      }
      onRunningChange(running)
    },
    [onRunningChange],
  )
  const metricsQuery = useMetricsList({
    select: (metrics) =>
      Object.values(metrics)
        .filter(isExpectedBehaviorCompositeMetric)
        .sort((left, right) =>
          left.metric_display_name.localeCompare(right.metric_display_name),
        ),
  })
  const metrics = useMemo(() => metricsQuery.data ?? [], [metricsQuery.data])
  const selectedMetric = useMemo(
    () => metrics.find(({ id }) => id === selectedMetricId) ?? null,
    [metrics, selectedMetricId],
  )
  const scope = useAnnotatedTestCaseConversationIds({
    enabled: Boolean(selectedMetric),
    metricId: selectedMetricId,
    testCaseId: testCase.id,
    testSetId,
  })

  const metricSelector = (
    <Select
      value={selectedMetricId}
      onValueChange={setSelectedMetricId}
      disabled={isRunning}
    >
      <SelectTrigger
        aria-label="Evaluation metric"
        className="h-8 min-w-0 flex-1"
      >
        <SelectValue placeholder="Choose a metric" />
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)] overflow-hidden">
        {metrics.map((metric) => (
          <SelectItem
            key={metric.id}
            value={metric.id}
            title={metric.metric_display_name}
          >
            <span className="block truncate">{metric.metric_display_name}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
  const expectedBehaviorSummary = (
    <ExpectedBehaviorScopeSummary testCase={displayedTestCase} />
  )

  if (!selectedMetric) {
    return (
      <aside
        aria-label="Test expected behaviors"
        className="flex h-full w-1/2 min-w-[30rem] shrink-0 flex-col overflow-hidden rounded-lg border border-coval-border bg-surface-default"
      >
        <div className="flex items-center gap-3 border-b border-coval-border p-4">
          <h2 className="shrink-0 text-base font-medium text-coval-text">
            Compare expected behaviors
          </h2>
          {metricsQuery.isLoading ? (
            <Skeleton className="h-8 min-w-48 flex-1 rounded-md" />
          ) : (
            metricSelector
          )}
          <Button
            type="button"
            variant="ghost"
            icon
            tooltip="Close drawer"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {expectedBehaviorSummary}
        <div className="flex flex-1 items-center justify-center p-6 text-sm text-coval-text-muted">
          Choose a metric to view annotated conversations for this test case.
        </div>
      </aside>
    )
  }

  return (
    <ScopedTestMetricDrawer
      key={`${testCase.id}:${selectedMetric.id}`}
      metric={selectedMetric}
      simulationIds={scope.simulationIds}
      annotationSimulationIds={scope.conversationIds}
      criteriaOverride={activeCriteriaSnapshot ?? testCase.expected_behaviors}
      experimentStorageScope={{
        testCaseId: testCase.id,
        expectedBehaviors:
          activeCriteriaSnapshot ?? testCase.expected_behaviors,
      }}
      beforeRun={persistExpectedBehaviors}
      onRunningChange={handleRunningChange}
      scopeContent={expectedBehaviorSummary}
      isLoadingScope={scope.isLoading}
      onClose={onClose}
      headerContent={metricSelector}
    />
  )
}
